{
  "title": "Services",
  "description": "Manage your services and track their performance",
  "create": "Create Service",
  "searchPlaceholder": "Search services...",
  "columns": {
    "status": "Status",
    "isPaid": "Is Paid",
    "ratings": "Ratings",
    "name": "Name",
    "provider": "Provider",
    "timeSlots": "Time Slots",
    "created": "Created",
    "order": "Order",
    "question": "Question",
    "type": "Type",
    "required": "Required"
  },
  "filters": {
    "paid": "Paid",
    "notPaid": "Not Paid",
    "active": "Active",
    "inactive": "Inactive"
  },
  "actions": {
    "activate": "Activate",
    "deactivate": "Deactivate",
    "activateSelected": "Activate Selected",
    "deleteSelected": "Delete Selected",
    "exportSelected": "Export Selected",
    "exporting": "Exporting {{count}} services",
    "quickActions": "Quick Actions",
    "editService": "Edit Service",
    "activateService": "Activate Service",
    "deleteService": "Delete Service",
    "addUnit": "Add Unit",
    "createUnit": "Create Unit",
    "importUnits": "Import Units",
    "learnMore": "Learn More",
    "editUnit": "Edit Unit",
    "availability": "Availability",
    "closures": "Closures",
    "deleteUnit": "Delete Unit",
    "addQuestion": "Add Question",
    "createQuestion": "Create Question",
    "importQuestions": "Import Questions",
    "addFirstUnit": "Add First Unit",
    "addFirstQuestion": "Add First Question",
    "addProvider": "Add Provider",
    "updateUnit": "Update Unit",
    "addUnitAction": "Add Unit",
    "updateQuestion": "Update Question",
    "addQuestionAction": "Add Question"
  },
  "show": {
    "title": "Service Details",
    "titleWithName": "{{name}} Service",
    "documentTitle": "{{name}} | Services",
    "loadingDescription": "Loading service details...",
    "tabs": {
      "basicInfo": "Basic Info",
      "serviceUnits": "Service Units",
      "closures": "Closures",
      "serviceQuestions": "Service Questions",
      "ratingsReviews": "Ratings & Reviews",
      "analytics": "Analytics"
    },
    "basicInfo": {
      "title": "Basic Info",
      "description": "View detailed information about this service",
      "serviceName": "Service Name",
      "descriptionLabel": "Description",
      "noDescription": "No description available",
      "status": "Status",
      "serviceType": "Service Type",
      "paidService": "Paid Service",
      "freeService": "Free Service",
      "price": "Price"
    },
    "ratingsReviews": {
      "title": "Ratings & Reviews",
      "description": "View customer ratings, reviews, and all services rendered by your business and providers",
      "servicesRendered": "Services Rendered"
    },
    "analytics": {
      "title": "Service Analytics",
      "description": "Comprehensive insights into your service performance, bookings, and customer satisfaction"
    },
    "statusLabels": {
      "active": "Active",
      "inactive": "Inactive"
    },
    "ratingStars": {
      "one": "1 Star",
      "other": "{{count}} Stars"
    }
  },
  "createPage": {
    "title": "Create Service",
    "description": "Follow the steps below to create a new service",
    "nextCreateService": "Create Service",
    "nextAddQuestions": "Add Questions",
    "incompleteServiceHint": "This service was created but is not finished yet.",
    "finishServiceLink": "Continue setup",
    "unitFailureHint": "Some units could not be saved. You can finish setup on the edit page:"
  },
  "editPage": {
    "title": "Edit Service",
    "titleWithName": "Edit {{name}} Service",
    "description": "Update the details for {{name}}",
    "documentTitle": "Edit {{name}}",
    "notFoundTitle": "Service Not Found",
    "notFoundDescription": "The service you are trying to edit could not be found."
  },
  "steps": {
    "basicInfo": {
      "title": "Basic Information",
      "createDescription": "Enter the basic information about your service",
      "editDescription": "Update the basic information about your service"
    },
    "units": {
      "title": "Service Units",
      "createDescription": "Add units to track availability and assign providers",
      "editDescription": "Manage units and assign providers"
    },
    "questions": {
      "title": "Service Questions",
      "createDescription": "Create questions for customer bookings",
      "editDescription": "Update questions for customer bookings"
    },
    "nav": {
      "nextAddUnits": "Next: Add Units",
      "nextAddQuestions": "Next: Add Questions",
      "previousBasicInfo": "Previous: Basic Info",
      "previousServiceUnits": "Previous: Service Units",
      "saveChanges": "Save Changes"
    }
  },
  "form": {
    "image": "Service Image",
    "imageDescription": "Upload an image for your service",
    "uploading": "Uploading image...",
    "name": "Service Name",
    "nameDescription": "Enter a clear, descriptive name for your service",
    "namePlaceholder": "Enter service name",
    "description": "Service Description",
    "descriptionHint": "Provide a detailed description of what your service offers",
    "descriptionPlaceholder": "Describe your service",
    "serviceType": "Service Type",
    "serviceTypeDescription": "Choose whether this is a free or paid service",
    "free": "Free",
    "paid": "Paid",
    "price": "Price (GH₵)",
    "priceDescription": "Set the price for your service",
    "pricePlaceholder": "0.00"
  },
  "units": {
    "title": "Service Units",
    "description": "Manage and track individual service units",
    "noProvidersTitle": "No providers yet",
    "noProvidersDescription": "Add a service provider before creating units. Providers are assigned to each unit.",
    "emptyTitle": "No Service Units Yet",
    "emptyDescription": "Add your first service unit to track availability and assign providers.",
    "emptyTableDescription": "You haven't created any service units yet. Get started by creating your first unit to track service availability.",
    "searchPlaceholder": "Search units...",
    "unknownProvider": "Unknown",
    "noTimeSlots": "No time slots",
    "moreSlots": "+{{count}} more",
    "timeSlotsCount": "{{count}} time slots",
    "timeSlotsConfigured": "{{count}} time slots configured",
    "configureTimeSlots": "Configure time slots",
    "unnamedUnit": "Unnamed Unit",
    "defaultUnitName": "Unit",
    "dialog": {
      "addTitle": "Add Service Unit",
      "editTitle": "Edit Service Unit",
      "addDescription": "Create a new service unit with provider and time slots.",
      "editDescription": "Update the service unit details.",
      "configureDescription": "Configure the unit details and availability schedule.",
      "unitName": "Unit Name",
      "unitNameDescription": "Enter a descriptive name for this service unit",
      "unitNameDescriptionShort": "Enter a name for this service unit",
      "unitNamePlaceholder": "Enter unit name",
      "provider": "Service Provider",
      "providerDescription": "Select the provider who will handle this unit",
      "providerDescriptionShort": "Select who will provide this service",
      "providerPlaceholder": "Select a provider",
      "noProvidersAvailable": "No providers available — add one under Service Providers",
      "timeSlots": "Time Slots",
      "timeSlotsDescription": "Configure when this unit is available for bookings",
      "timeSlotsDescriptionShort": "Configure availability for this unit",
      "creating": "Creating...",
      "updating": "Updating..."
    }
  },
  "questions": {
    "title": "Service Questions",
    "description": "Manage questions for service bookings",
    "emptyTitle": "No Questions Yet",
    "emptyDescription": "Add questions to gather information from customers during booking.",
    "emptyTableTitle": "No Service Questions Yet",
    "emptyTableDescription": "You haven't created any service questions yet. Get started by creating your first question to gather information from customers during booking.",
    "searchPlaceholder": "Search questions...",
    "optionsCount": "{{count}} options",
    "optional": "Optional",
    "requiredBadge": "Required",
    "dialog": {
      "addTitle": "Add Question",
      "editTitle": "Edit Question",
      "description": "Configure the question details and type.",
      "questionText": "Question Text",
      "questionTextDescription": "Enter the question you want to ask customers",
      "questionTextPlaceholder": "Enter your question",
      "questionType": "Question Type",
      "questionTypeDescription": "Choose how customers will answer this question",
      "answerOptions": "Answer Options",
      "answerOptionsDescription": "Add the available options for this question",
      "optionPlaceholder": "Option {{number}}",
      "addOption": "Add Option",
      "requiredQuestion": "Required Question",
      "requiredQuestionDescription": "Make this question mandatory for customers to answer"
    },
    "types": {
      "text": "Short Text",
      "textarea": "Long Text",
      "select": "Dropdown",
      "radio": "Multiple Choice",
      "checkbox": "Checkboxes",
      "date": "Date",
      "time": "Time"
    }
  },
  "closures": {
    "add": "Add",
    "title": "Closures & Special Days",
    "description": "Manage service-wide closed dates and days with custom operating hours. These apply to all service units unless a unit has its own closure.",
    "blockedDays": {
      "title": "Blocked days (closed)",
      "description": "Dates when this service is fully closed for all units.",
      "emptyTitle": "No blocked days",
      "emptyDescription": "Add dates when this service will be closed for customers.",
      "add": "Add blocked day",
      "dialogTitle": "Add blocked day",
      "dialogDescription": "Customers will not be able to book this service on the selected date.",
      "reasonOptional": "Reason (optional)",
      "reasonPlaceholder": "Public holiday",
      "removeAriaLabel": "Remove blocked day"
    },
    "specialDays": {
      "title": "Special days (custom hours)",
      "description": "Dates with different opening hours than the usual schedule.",
      "emptyTitle": "No special days",
      "emptyDescription": "Add dates with custom hours, such as early closing or extended hours.",
      "add": "Add special day",
      "dialogTitle": "Add special day",
      "dialogDescription": "Set custom operating hours for a specific date across all units.",
      "opens": "Opens",
      "closes": "Closes",
      "reasonPlaceholder": "Early closing",
      "removeAriaLabel": "Remove special day"
    },
    "unitDialog": {
      "title": "Provider closures — {{name}}",
      "description": "Block specific dates for this service unit and provider only.",
      "reasonPlaceholder": "Provider day off",
      "loading": "Loading...",
      "noBlockedDays": "No blocked days"
    },
    "fields": {
      "date": "Date"
    }
  },
  "scheduler": {
    "title": "Configure time slots",
    "description": "These slots apply only to this provider. Other providers on the same service are independent — we do not assume a shared chair or room. Use any start and end time (for example 12:00–12:15).",
    "empty": "No slots yet. Add the times customers can book with this provider.",
    "addSlot": "Add slot",
    "done": "Done",
    "editSlot": "Edit time slot",
    "addSlotTitle": "Add time slot",
    "slotDescription": "Set the day and exact times for this bookable window.",
    "day": "Day",
    "startTime": "Start time",
    "endTime": "End time",
    "overlapHint": "Only slots in this unit are checked for overlaps. Other providers on the same service can use the same times.",
    "removeSlot": "Remove slot",
    "saveSlot": "Save slot",
    "editSlotSr": "Edit slot",
    "removeSlotSr": "Remove slot",
    "days": {
      "monday": "Monday",
      "tuesday": "Tuesday",
      "wednesday": "Wednesday",
      "thursday": "Thursday",
      "friday": "Friday",
      "saturday": "Saturday",
      "sunday": "Sunday"
    }
  },
  "analytics": {
    "kpi": {
      "totalBookings": "Total Bookings",
      "totalBookingsDescription": "All time bookings",
      "totalRevenue": "Total Revenue",
      "totalRevenueDescription": "Total earnings",
      "averageRating": "Average Rating",
      "averageRatingDescription": "Customer satisfaction",
      "fulfillmentRate": "Fulfillment Rate",
      "fulfillmentRateDescription": "Service completion rate",
      "changeFromLastPeriod": "{{change}}% from last period"
    },
    "charts": {
      "noData": "No data",
      "noDataProvider": "No Data",
      "bookingsTimeline": {
        "title": "Bookings Timeline",
        "description": "Showing completed and cancelled bookings for the last 6 months",
        "trendUp": "Trending up by {{change}}% this month",
        "trendDown": "Trending down by {{change}}% this month"
      },
      "appointmentStatus": {
        "title": "Appointment Status",
        "description": "Distribution of appointment statuses",
        "totalAppointments": "Total appointments: {{count}}"
      },
      "revenue": {
        "title": "Revenue",
        "description": "Revenue over time"
      },
      "providerPerformance": {
        "title": "Provider Performance",
        "description": "Revenue and bookings by provider",
        "totalRevenueFooter": "Total revenue: GH₵{{amount}}"
      },
      "ratingsDistribution": {
        "title": "Ratings Distribution",
        "description": "Breakdown of customer ratings",
        "basedOnReviews": "Based on {{count}} total reviews"
      },
      "peakHours": {
        "title": "Peak Hours",
        "description": "Busiest booking hours",
        "peakTime": "Peak booking time: {{hour}}"
      },
      "providerBookings": {
        "title": "Provider Bookings",
        "description": "Bookings per provider",
        "topPerformer": "Top performer: {{name}} with {{count}} bookings"
      },
      "providerUtilization": {
        "title": "Provider Utilization",
        "description": "Provider capacity usage",
        "footer": "Average utilization: {{avg}}% | Highest: {{name}} ({{value}}%)"
      },
      "providerRatings": {
        "title": "Provider Ratings",
        "description": "Average ratings by provider",
        "footer": "Average rating: {{avg}}/5 | Top rated: {{name}} ({{rating}}/5)"
      },
      "totalRatings": {
        "title": "Total Ratings",
        "description": "All rating submissions",
        "descriptionOverTime": "Ratings received over time"
      },
      "fulfillmentRate": {
        "title": "Fulfillment Rate",
        "description": "Completed vs total bookings",
        "descriptionOverTime": "Service completion rate over time"
      },
      "labels": {
        "completed": "Completed",
        "cancelled": "Cancelled",
        "pending": "Pending",
        "rescheduled": "Rescheduled",
        "revenue": "Revenue",
        "bookings": "Bookings",
        "rating": "Rating",
        "utilization": "Utilization",
        "reviews": "Reviews",
        "fulfillmentRate": "Fulfillment Rate"
      }
    }
  },
  "reviews": {
    "averageRating": "Average Rating",
    "outOfFive": "out of 5.0",
    "totalReviews": "Total Reviews",
    "customerReviews": "Customer reviews",
    "totalRatings": "Total Ratings",
    "individualSubmissions": "Individual rating submissions"
  },
  "rendered": {
    "searchPlaceholder": "Search services rendered...",
    "emptyTitle": "No Services Rendered Yet",
    "emptyDescription": "Services rendered by your business and providers will appear here.",
    "filters": {
      "all": "All",
      "completed": "Completed",
      "pending": "Pending",
      "cancelled": "Cancelled"
    },
    "columns": {
      "customer": "Customer",
      "service": "Service",
      "provider": "Provider",
      "date": "Date",
      "time": "Time",
      "status": "Status",
      "rating": "Rating"
    }
  },
  "validation": {
    "nameRequired": "Service name is required",
    "descriptionRequired": "Service description is required",
    "imageRequired": "Service image is required",
    "priceRequired": "Price must be greater than 0 for paid services",
    "imageUploadIncomplete": "Image upload failed or incomplete. Please re-upload the image.",
    "unitNameRequired": "Enter a name for this service unit.",
    "providerRequired": "Select a service provider for this unit.",
    "serviceLoadFailed": "This service could not be loaded. Refresh the page and try again.",
    "businessLoadFailed": "Your business could not be loaded. Refresh the page and try again.",
    "atLeastOneUnit": "Add at least one service unit before continuing.",
    "unitTimeslotsRequired": "Add time slots for \"{{name}}\".",
    "slotOverlap": "Two slots on {{day}} overlap ({{startA}}–{{endA}} and {{startB}}–{{endB}}). Adjust the times for this provider.",
    "singleSlotOverlap": "This overlaps with {{start}}–{{end}} on {{day}}. Choose a different time for this provider.",
    "invalidTimes": "Enter a valid start and end time (HH:MM).",
    "endBeforeStart": "End time must be after start time.",
    "overlapsFound": "Two or more time slots on the same day overlap. Change the times so this provider is not scheduled twice.",
    "unitNotFound": "This service unit was not found. Refresh the page and try again.",
    "providerNotFound": "The selected provider could not be found. Choose another provider.",
    "notAProvider": "The selected user is not a service provider. Choose someone from your provider list.",
    "businessNotFound": "Your business profile could not be found. Refresh and try again.",
    "serviceNotFound": "This service could not be found. Refresh the page and try again.",
    "serviceNotInBusiness": "This service does not belong to your business.",
    "questionTextRequired": "Please enter the question text",
    "questionOptionsRequired": "Please add at least one option"
  },
  "toasts": {
    "uploadFailed": "Failed to upload image",
    "imageRequired": "Service image is required",
    "imageTooLarge": "The uploaded image is too large. Please try a smaller file (under 5MB).",
    "created": "Service created successfully!",
    "createdFull": "Service created successfully with all details!",
    "createFailed": "Failed to create service",
    "createUnitsFailed": "Failed to create service units",
    "updated": "Service updated successfully!",
    "updateFailed": "Failed to update service",
    "businessUnknown": "Could not determine the business for this service",
    "questionsSaveFailed": "Could not save service questions. Try again.",
    "unitAvailabilityUpdated": "Unit availability updated successfully",
    "timeslotsSaveFailed": "Could not save time slots. Try again.",
    "unitUpdated": "Service unit updated successfully",
    "unitCreated": "Service unit created successfully",
    "unitUpdateFailed": "Could not update this service unit. Try again.",
    "unitCreateFailed": "Could not create this service unit. Try again.",
    "unitDeleted": "Unit deleted successfully",
    "unitDeleteFailed": "Could not delete this service unit.",
    "questionUpdated": "Question updated successfully",
    "questionAdded": "Question added successfully",
    "questionDeleted": "Question deleted successfully",
    "blockedDayAdded": "Blocked day added",
    "blockedDayAddFailed": "Failed to add blocked day",
    "specialDayAdded": "Special day added",
    "specialDayAddFailed": "Failed to add special day",
    "blockedDayRemoved": "Blocked day removed",
    "blockedDayRemoveFailed": "Failed to remove blocked day",
    "specialDayRemoved": "Special day removed",
    "specialDayRemoveFailed": "Failed to remove special day",
    "providerBlockedDayAdded": "Provider blocked day added"
  },
  "dialogs": {
    "activateTitle": "Activate Service",
    "activateTitleBulk": "Activate {{count}} Services",
    "activateDescription": "Are you sure you want to activate \"{{name}}\"? It will become visible to customers.",
    "activateDescriptionBulk": "Are you sure you want to activate {{count}} services? They will become visible to customers.",
    "activateConfirm": "Activate",
    "activated": "Service activated successfully",
    "activatedBulk": "{{count}} services activated successfully",
    "activateError": "Failed to activate service",
    "deactivateTitle": "Deactivate Service",
    "deactivateTitleBulk": "Deactivate {{count}} Services",
    "deactivateDescription": "Are you sure you want to deactivate \"{{name}}\"? It will no longer be visible to customers.",
    "deactivateDescriptionBulk": "Are you sure you want to deactivate {{count}} services? They will no longer be visible to customers.",
    "deactivateConfirm": "Deactivate",
    "deactivated": "Service deactivated successfully",
    "deactivatedBulk": "{{count}} services deactivated successfully",
    "deactivateError": "Failed to deactivate service",
    "deleteTitle": "Delete Service",
    "deleteTitleBulk": "Delete {{count}} Services",
    "deleteDescription": "Are you sure you want to delete \"{{name}}\"? This action cannot be undone.",
    "deleteDescriptionBulk": "Are you sure you want to delete {{count}} services? This action cannot be undone.",
    "deleted": "Service deleted successfully",
    "deletedBulk": "{{count}} services deleted successfully",
    "deleteError": "Failed to delete service",
    "passwordRequired": "Password is required",
    "passwordLabel": "Password",
    "passwordPlaceholder": "Enter your password to confirm"
  }
}
